import { SOIL_SLOT } from "../render/plot";

/**
 * FENCE GEOMETRY — the lattice a fence stands on, and the one question every other system asks of it:
 * "which fences come within r of this point?". Pure geometry with no game imports, so the placement rules
 * in sim/garden.ts can treat a fence as a solid line without importing sim/fence.ts — which imports
 * garden.ts for the water test and the refund rate, and would otherwise close an import cycle.
 *
 * Geometry: a soil slot is CENTRED at (sx·SOIL_SLOT, sy·SOIL_SLOT) and spans ±SOIL_SLOT/2, so slot
 * BOUNDARY lines sit at HALF-slot offsets, (k+0.5)·SOIL_SLOT. Fence POSTS live on the lattice of slot
 * corners: post (px,py) is at garden ((px+0.5)·SOIL_SLOT, (py+0.5)·SOIL_SLOT). An edge is keyed by its
 * lower-index post + direction:
 *   • "h" = edge from post (sx,sy) to (sx, sy+1) — runs in +y (constant gx = a slot boundary).
 *   • "v" = edge from post (sx,sy) to (sx+1, sy) — runs in +x (constant gy = a slot boundary).
 */

const GRID = SOIL_SLOT;

/** HALF THE FENCE'S THICKNESS ON THE GROUND (garden units) — the post's foot, the widest part of it that
 *  stands on the turf. The renderer's FENCE_POST_W (12 px half-width at the foot) is a horizontal screen
 *  span, which is 12/46·√½ ≈ 0.18 garden units; rounded up so a heavier corner post is covered too. This
 *  is what makes a fence SOLID to placement: nothing's footprint may come closer to the line than this. */
export const FENCE_HALF_T = 0.2;

/** Garden-unit position of fence POST (px,py) — a soil-slot CORNER (half-slot offsets). */
export function post(px: number, py: number): { gx: number; gy: number } {
  return { gx: (px + 0.5) * GRID, gy: (py + 0.5) * GRID };
}

export type FenceDir = "h" | "v";
export interface FenceEdge { sx: number; sy: number; dir: FenceDir; }

export function fenceKey(sx: number, sy: number, dir: FenceDir): string { return `${sx},${sy},${dir}`; }
export function edgeKey(e: FenceEdge): string { return fenceKey(e.sx, e.sy, e.dir); }
export function parseFenceKey(key: string): FenceEdge | null {
  const m = /^(-?\d+),(-?\d+),([hv])$/.exec(key);
  if (!m) return null;
  return { sx: parseInt(m[1]!, 10), sy: parseInt(m[2]!, 10), dir: m[3] as FenceDir };
}

/** The two garden-unit ENDPOINTS (posts) of a fence edge. */
export function edgeEnds(e: FenceEdge): [{ gx: number; gy: number }, { gx: number; gy: number }] {
  return e.dir === "h"
    ? [post(e.sx, e.sy), post(e.sx, e.sy + 1)]
    : [post(e.sx, e.sy), post(e.sx + 1, e.sy)];
}
/** The garden-unit CENTRE point (midpoint) of a fence edge. */
export function edgeCenter(e: FenceEdge): { gx: number; gy: number } {
  const [a, b] = edgeEnds(e);
  return { gx: (a.gx + b.gx) / 2, gy: (a.gy + b.gy) / 2 };
}

/** A placed fence near a point: its key, and the NEAREST POINT on its centre line with that distance. */
export interface FenceHit { key: string; nx: number; ny: number; d: number; }

/**
 * Every PLACED fence whose centre line passes within `reach` garden units of (gx, gy), nearest point and
 * distance included. Walks only the lattice edges that could reach — never the whole set — so it is safe
 * on every pointer move and inside the placement loops.
 */
export function fencesNear(fences: ReadonlySet<string> | undefined, gx: number, gy: number, reach: number): FenceHit[] {
  const out: FenceHit[] = [];
  if (!fences || fences.size === 0) return out;
  // post-lattice coords: post p sits at (p+0.5)·GRID, so every edge touching the reach box has a lower
  // post index in [floor((v−reach)/GRID − 0.5) − 1, floor((v+reach)/GRID − 0.5)] on each axis.
  const lo = (v: number) => Math.floor((v - reach) / GRID - 0.5) - 1;
  const hi = (v: number) => Math.floor((v + reach) / GRID - 0.5);
  const r2 = reach * reach;
  for (let sx = lo(gx); sx <= hi(gx); sx++) {
    for (let sy = lo(gy); sy <= hi(gy); sy++) {
      for (const dir of ["h", "v"] as const) {
        const key = fenceKey(sx, sy, dir);
        if (!fences.has(key)) continue;
        const [a, b] = edgeEnds({ sx, sy, dir });
        // clamp the point onto the axis-aligned segment
        const nx = Math.min(Math.max(gx, Math.min(a.gx, b.gx)), Math.max(a.gx, b.gx));
        const ny = Math.min(Math.max(gy, Math.min(a.gy, b.gy)), Math.max(a.gy, b.gy));
        const d2 = (gx - nx) ** 2 + (gy - ny) ** 2;
        if (d2 < r2) out.push({ key, nx, ny, d: Math.sqrt(d2) });
      }
    }
  }
  return out;
}

/** Does a disc of radius `r` at (gx, gy) run into a placed fence (its centre line, thickened by
 *  FENCE_HALF_T)? The placement rules' one fence test — see canPlaceAt / decorPlaceBlock. */
export function discHitsFence(fences: ReadonlySet<string> | undefined, gx: number, gy: number, r: number): boolean {
  return fencesNear(fences, gx, gy, r + FENCE_HALF_T).length > 0;
}
